import type { ClientError, NetworkError, ServerError } from "@axios-extensions/plugins-api";
import { InterceptorType } from "@axios-extensions/plugins-api";
import { installInterceptor } from "@verbleif/shared/src/composables/Api/api";
import { isGuestLinkRequest } from "@verbleif/shared/src/composables/Api/guestLink";
import { useImpersonationStore } from "@web/features/Auth/Impersonate/useImpersonationStore";

const EXCLUDED_ROUTES = ["/oauth/v2/token", "legacy_notification_user_subscriptions"];

export const impersonationInterceptorPlugin = {
  name: "ImpersonationInterceptor",
  type: InterceptorType.REQUEST,
  priority: 1001, // Run after HeaderRequestInterceptor (priority 1000) to ensure we can remove headers
  resolved: async (config: any) => {
    if (!config.url) {
      return config;
    }

    // Ensure headers object exists
    if (!config.headers) {
      config.headers = {};
    }

    const url = String(config.url);

    // Skip impersonation for excluded routes
    // Check for legacy_notification_user_subscriptions endpoint (should not include x-switch-user header)
    if (url.includes("legacy_notification_user_subscriptions")) {
      delete config.headers["x-switch-user"];
      delete config.headers["X-Switch-User"];
      if (config.headers.common) {
        delete config.headers.common["x-switch-user"];
        delete config.headers.common["X-Switch-User"];
      }
      return config;
    }
    if (EXCLUDED_ROUTES.some(route => url.includes(route))) {
      return config;
    }
    // A guest link call acts for the guest link alone: the public API would refuse the switch (403).
    if (isGuestLinkRequest(config)) {
      return config;
    }

    const impersonationStore = useImpersonationStore();

    if (!impersonationStore.isImpersonating.value) {
      return config;
    }

    Object.assign(config.headers, impersonationStore.getRequestHeaders.value);

    return config;
  },
};

installInterceptor(impersonationInterceptorPlugin);

export const impersonationResponseInterceptorPlugin = {
  name: "ImpersonationResponseInterceptor",
  type: InterceptorType.RESPONSE,
  priority: 10,
  rejected: async (failedRequest: ClientError | ServerError | NetworkError) => {
    const impersonationStore = useImpersonationStore();

    if (!impersonationStore.isImpersonating.value) {
      throw failedRequest;
    }

    if (!failedRequest.response || failedRequest.response.status !== 403) {
      throw failedRequest;
    }

    // A guest link call never carries the switch, so its 403 says nothing about the impersonation.
    if (failedRequest.config && isGuestLinkRequest(failedRequest.config)) {
      throw failedRequest;
    }

    // Switch User failed: User, check if data.details contatins "Switch User failed: User"
    if (
      !failedRequest.response?.data?.detail?.includes(
        "Switch User failed: User",
      )
    ) {
      throw failedRequest;
    }

    impersonationStore.reset();
    impersonationStore.showImpersonateError.value = failedRequest.response?.data?.detail ?? "Unknown error";
    throw failedRequest;
  },
};

installInterceptor(impersonationResponseInterceptorPlugin);
